import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from 'lucide-react';
import { LongTermProjection } from '@/types/analytics';
import { formatCurrency } from '@/components/calculator/utils/formatters';
import { MetricTooltip } from './MetricTooltip';

interface LongTermProjectionCardProps {
  projection: LongTermProjection;
}

export const LongTermProjectionCard: React.FC<LongTermProjectionCardProps> = ({ projection }) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
           <Calendar className="w-5 h-5" />
          Long-Term Financial Projection
          <MetricTooltip metric="longTermProjection" />
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="text-left p-2">Years</th>
                 <th className="text-right p-2">Total Paid <MetricTooltip metric="totalPaid" iconSize={12} /></th>
                <th className="text-right p-2">Principal <MetricTooltip metric="principalPaid" iconSize={12} /></th>
                <th className="text-right p-2">Interest <MetricTooltip metric="interestPaid" iconSize={12} /></th>
                <th className="text-right p-2">Balance <MetricTooltip metric="remainingBalance" iconSize={12} /></th>
                <th className="text-right p-2">Home Equity <MetricTooltip metric="homeEquity" iconSize={12} /></th>
              </tr>
            </thead>
            <tbody>
              {projection.milestones.map(milestone => (
                <tr key={milestone.years} className="border-b hover:bg-muted/50">
                  <td className="p-2 font-medium">{milestone.years}</td>
                  <td className="text-right p-2">{formatCurrency(milestone.totalPaid)}</td>
                  <td className="text-right p-2 text-green-600">{formatCurrency(milestone.principalPaid)}</td>
                  <td className="text-right p-2 text-red-600">{formatCurrency(milestone.interestPaid)}</td>
                  <td className="text-right p-2">{formatCurrency(milestone.remainingBalance)}</td>
                  <td className="text-right p-2 font-semibold text-blue-600">{formatCurrency(milestone.homeEquity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900">
          <div className="flex justify-between items-center">
            <div>
               <p className="text-sm text-muted-foreground">Projected Net Worth Impact <MetricTooltip metric="netWorthImpact" iconSize={12} /></p>
              <p className="text-xs text-muted-foreground mt-1">
                Assuming {projection.appreciationRate}% annual appreciation <MetricTooltip metric="appreciationRate" iconSize={12} />
              </p>
            </div>
            <p className="text-2xl font-bold text-green-600">
              {formatCurrency(projection.netWorthImpact)}
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
